<template>
  <ResponsiveDrawer :ref="drawerRef" :width="304" breakpoint="lg">
    <template #header-extra>
      <button class="MenuWrapper__logoAndAssistant" @click="whenClickLogo">
        <div class="MenuWrapper__logoAndAssistantWrapper">
          <img :src="logo" alt="logo" class="MenuWrapper__icon" />
          <img :src="assistant" alt="assistant" class="MenuWrapper__icon" />
        </div>
      </button>
    </template>
    <aside class="MenuWrapper">
      <div class="MenuWrapper__mainItemsWrapper">
        <MenuItem
          v-for="({ label, key, icon }, index) in items"
          :key="index"
          :label="label"
          :leftContentIcon="icon"
          :whenClick="() => whenMenuItemClick(key)"
          :selectedRoute="`${selectedKey}`"
          :eventKey="EVENT_KEY_TO_ROUTE_ADAPTER[key] || key"
        />
      </div>
      <ThreadsWrapper :whenClickItem="whenClickThread" :selectedThreadId="selectedThreadId" />
      <div class="MenuWrapper__profileItemsWrapper">
        <ProfileMenu />
      </div>
    </aside>
  </ResponsiveDrawer>
  <Overlay :is-open="isShowModalSearch" :onClose="closeModalSearch">
    <ThreadSearch :on-close="closeModalSearch" />
  </Overlay>
</template>

<script lang="ts" setup>
import { ref, computed, reactive, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import folder from '@/assets/icons/menu/folder.svg'
import dialog from '@/assets/icons/menu/dialog.svg'
import search from '@/assets/icons/menu/search.svg'

import logo from '@/assets/icons/menu/logo.svg'
import assistant from '@/assets/icons/menu/assistant.svg'
import MenuItem from './MenuItem.vue'
import { Routes } from '@/config/router/routes'

import { ThreadsWrapper } from '@/ui/common/layout/threads-wrapper'
import { EventKeys } from '@/ui/common/layout/menu/types'
import Overlay from '@/ui/common/overlay/overlay.vue'

import { useOverlayStore } from '@/store/overlay-store'
import { storeToRefs } from 'pinia'
import { useUIStore } from '@/store/ui-store'
import { ThreadSearch } from '@/ui/common/search/index'

import ResponsiveDrawer from '@/ui/common/layout/ResponsiveDrawer.vue'

import { ProfileMenu } from '../../profile-menu'

import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const router = useRouter()

const route = useRoute()

const drawerRef = ref()

const uiStore = useUIStore()()

const { toggleDrawer } = uiStore

const { isMobile } = storeToRefs(uiStore)

const overlayStore = useOverlayStore()

const { closeModalSearch, openModalSearch } = overlayStore

const { isShowModalSearch } = storeToRefs(overlayStore)

const selectedKey = ref<string>(route.name as string)

const selectedThreadId = computed((): string => {
  return selectedKey.value?.includes(Routes.ChatDetail) ? (route.params.id as string) || '' : ''
})

watch(
  () => route.name,
  (newRouteName) => newRouteName && (selectedKey.value = newRouteName as string),
  { immediate: true, deep: true },
)

interface IMenuItem {
  label: string
  key: EventKeys
  icon?: any
  type?: 'group'
}

function getItem(label: string, key: EventKeys, icon?: any, type?: 'group'): IMenuItem {
  return {
    key,
    icon,
    label,
    type,
  }
}

const CHAT_LABELS = computed<Record<EventKeys, string>>(() => ({
  [EventKeys.NewChat]: t('menu.new-chat'),
  [EventKeys.ChatSearch]: t('menu.search-chat'),
  [EventKeys.ChatHistory]: t('menu.history-chat'),
}))

const CHAT_ICONS: Record<EventKeys, string> = {
  [EventKeys.NewChat]: dialog,
  [EventKeys.ChatSearch]: search,
  [EventKeys.ChatHistory]: folder,
}

const EVENT_KEY_TO_ROUTE_ADAPTER: Partial<Record<EventKeys, Routes>> = {
  [EventKeys.NewChat]: Routes.Chat,
  [EventKeys.ChatHistory]: Routes.ChatsHistory,
}

const items = computed<IMenuItem[]>(() => [
  getItem(CHAT_LABELS.value[EventKeys.NewChat], EventKeys.NewChat, CHAT_ICONS[EventKeys.NewChat]),
  getItem(
    CHAT_LABELS.value[EventKeys.ChatSearch],
    EventKeys.ChatSearch,
    CHAT_ICONS[EventKeys.ChatSearch],
  ),
  getItem(
    CHAT_LABELS.value[EventKeys.ChatHistory],
    EventKeys.ChatHistory,
    CHAT_ICONS[EventKeys.ChatHistory],
  ),
])

const actionByEventKey: Record<EventKeys, () => void> = {
  [EventKeys.ChatHistory]: () => router.push({ name: Routes.ChatsHistory }),
  [EventKeys.ChatSearch]: openModalSearch,
  [EventKeys.NewChat]: () => router.push({ name: Routes.Chat }),
}
const whenMenuItemClick = (value: EventKeys) => {
  actionByEventKey[value]()

  if (isMobile) {
    toggleDrawer(false)
  }
}

const whenClickThread = (id: string) => {
  selectedKey.value = `${Routes.Chat}/${id}`

  if (isMobile) {
    toggleDrawer(false)
  }

  router.push({
    name: Routes.ChatDetail,
    params: { id },
  })
}

const whenClickLogo = () => router.push({ name: Routes.Chat })
</script>

<style lang="scss" scoped>
.MenuWrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 304px;
  &__icon {
    height: 24px;
  }

  &__logoAndAssistant {
    width: 100%;

    &Wrapper {
      display: flex;
      align-items: center;
      padding: 16px 24px;
      gap: 8px;
      cursor: pointer;
    }
  }

  &__mainItemsWrapper {
    padding: 12px;
    border-bottom: 1px solid var(--gray_20);
  }

  &__profileItemsWrapper {
    border-top: 1px solid var(--gray_20);
    display: flex;
    align-items: center;
    padding: 12px;
    gap: 8px;
  }
}
</style>
